import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useMemo, useRef } from 'react';
import { onIdTokenChanged, signOut, User } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { getUserRole } from '../graphqlService';
import { client } from '../apolloClient';
import { TabCoordinatedSessionManager } from '../services/TabCoordinatedSessionManager';
import { debugLog } from '../utils/debugLog';
import { performLogout, readLoggedOutAt, shouldReloadRestoredPage } from '../utils/logout';

/**
 * Must stay in step with SELF_ASSIGNABLE_ROLES in the backend's resolvers.
 * The backend accepts 'editor,creator' and 'user' as well; omitting them here
 * meant isValidRole rejected those stored roles, setUserRole was never called,
 * and the user ended up with NO role at all — silently falling through every
 * role gate in the app.
 */
export type UserRole =
  | 'superadmin'
  | 'creator'
  | 'creator,editor'
  | 'editor,creator'
  | 'editor'
  | 'user';

export interface AuthUser extends User {
  role?: UserRole;
}

interface AuthContextProps {
  user: AuthUser | null;
  loading: boolean;
  /**
   * True while a signed-in user's role is still being fetched. `loading`
   * covers only Firebase's sign-in state; the role arrives afterwards, so
   * anything that depends on it (role-gated routes, the menu) waits for this.
   * A failed fetch ends it too, with no role (treated as a creator).
   */
  roleLoading: boolean;
  getToken: () => Promise<string | null>;
  refreshUserRole: () => Promise<void>;
  sessionExpired: boolean;
  handleSessionExpiry: () => void;
  refreshSession: () => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: true,
  roleLoading: false,
  getToken: async () => null,
  refreshUserRole: async () => {},
  sessionExpired: false,
  handleSessionExpiry: () => {},
  refreshSession: async () => {},
});

export const isValidRole = (role: string): role is UserRole => {
  return ['superadmin', 'creator', 'creator,editor', 'editor,creator', 'editor', 'user'].includes(role);
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Firebase's User keeps getIdToken/reload on its prototype, so spreading it
  // into a new object literal silently drops those methods. Keep the live
  // instance in state and carry the role alongside it instead.
  const [baseUser, setBaseUser] = useState<AuthUser | null>(null);
  const [userRole, setUserRole] = useState<UserRole | undefined>(undefined);
  // The uid whose role fetch has finished (successfully or not).
  const [roleSettledForUid, setRoleSettledForUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const user = useMemo<AuthUser | null>(() => {
    if (!baseUser) return null;
    baseUser.role = userRole;
    return baseUser;
  }, [baseUser, userRole]);

  // Which uid we have already fetched a role for, so routine token refreshes
  // don't re-query it.
  const roleLoadedForUidRef = useRef<string | null>(null);

  // Which uid has already had its verification state reloaded this session.
  // Deliberately NOT cleared by clearUser() (which also runs for unverified
  // users); only an actual sign-out resets it. See the listener below.
  const verificationReloadedForUidRef = useRef<string | null>(null);
  const verifiedTokenRefreshedForUidRef = useRef<string | null>(null);

  // When this page last showed a verified, signed-in user. Compared with the
  // shared logged-out timestamp when the page comes back from the bfcache.
  const lastSignedInSeenAtRef = useRef(0);

  const clearUser = useCallback(() => {
    setBaseUser(null);
    setUserRole(undefined);
    setRoleSettledForUid(null);
    roleLoadedForUidRef.current = null;
  }, []);
  const [sessionExpired, setSessionExpired] = useState(false);

  const isE2EAuthBypassEnabled = (() => {
    if (process.env.NODE_ENV === 'production') return false;
    if (process.env.REACT_APP_E2E_AUTH_BYPASS !== '1') return false;
    try {
      return window.localStorage.getItem('pithplay_e2e_auth_bypass') === '1';
    } catch {
      return false;
    }
  })();

  const [sessionManager] = useState(() => {
    return isE2EAuthBypassEnabled ? null : TabCoordinatedSessionManager.getInstance();
  });

  const loadUserRole = useCallback(async (uid: string) => {
    try {
      const role = await getUserRole(uid);
      if (isValidRole(role)) {
        setUserRole(role);
      } else {
        console.warn('Received unrecognised user role from backend:', role);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    } finally {
      setRoleSettledForUid(uid);
    }
  }, []);

  useEffect(() => {
    if (isE2EAuthBypassEnabled) {
      const uid = (() => {
        try {
          return window.localStorage.getItem('pithplay_e2e_uid') || 'e2e-user';
        } catch {
          return 'e2e-user';
        }
      })();
      const email = (() => {
        try {
          return window.localStorage.getItem('pithplay_e2e_email') || 'e2e.user@pithplay.local';
        } catch {
          return 'e2e.user@pithplay.local';
        }
      })();
      const role = (() => {
        try {
          return window.localStorage.getItem('pithplay_e2e_role') || 'creator';
        } catch {
          return 'creator';
        }
      })();

      const mockUser = {
        uid,
        email,
        emailVerified: true,
        getIdToken: async () => 'e2e-test-token',
        role
      } as unknown as AuthUser;

      setBaseUser(mockUser);
      setUserRole(isValidRole(role) ? role : 'creator');
      setRoleSettledForUid(uid);
      setLoading(false);
      return;
    }

    // onIdTokenChanged, NOT onAuthStateChanged.
    //
    // onAuthStateChanged only fires when the UID changes (see
    // notifyAuthListeners in @firebase/auth: it guards on lastNotifiedUid).
    // reload() and getIdToken(true) do not change the UID, so after a user
    // verifies their email — an invited editor finishing registration, or
    // anyone clicking "I've verified my email" — the listener never re-fired,
    // `user` stayed null, and PrivateRoute bounced them to /login seconds
    // after they set a password. onIdTokenChanged fires on every token
    // change as well as sign-in/out, so verification is picked up.
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          // emailVerified can be stale: Firebase persists the user snapshot
          // locally, and server-side verification (verify-email link, invited
          // editors verified via the Admin SDK) doesn't refresh it. Reload
          // once per uid per session before deciding to restrict access.
          //
          // ONCE: reload() ends by notifying this very listener (Firebase's
          // _notifyListenersIfCurrent), so reloading on every notification
          // looped - an endless stream of accounts:lookup calls for as long
          // as an unverified user stayed signed in (which they do, for the
          // resend flow), until Firebase throttled the browser and sign-in
          // failed with auth/network-request-failed. The uid is recorded
          // BEFORE the reload so that re-entrant notification skips it. The
          // "I've Verified My Email" button reloads the user itself, which
          // re-fires this listener with the fresh emailVerified.
          if (!firebaseUser.emailVerified && verificationReloadedForUidRef.current !== firebaseUser.uid) {
            verificationReloadedForUidRef.current = firebaseUser.uid;
            try {
              await firebaseUser.reload();
            } catch (reloadError) {
              console.warn('Failed to refresh user verification state:', reloadError);
            }
          }
          // Just after verifying, the sign-in token still says the email is
          // unverified until it is next refreshed (up to an hour), and the
          // server only allows creator actions and matches invitations for a
          // verified email. Refresh it once; that re-fires this listener.
          if (
            firebaseUser.emailVerified &&
            verifiedTokenRefreshedForUidRef.current !== firebaseUser.uid &&
            typeof firebaseUser.getIdTokenResult === 'function'
          ) {
            // Recorded before waiting, so the notifications this causes skip it.
            verifiedTokenRefreshedForUidRef.current = firebaseUser.uid;
            try {
              const { claims } = await firebaseUser.getIdTokenResult();
              if (claims.email_verified !== true) {
                await firebaseUser.getIdToken(true);
              }
            } catch (refreshError) {
              console.warn('Failed to refresh the sign-in token after verification:', refreshError);
            }
          }
          // Only set user if email is verified
          if (firebaseUser.emailVerified) {
            lastSignedInSeenAtRef.current = Date.now();
            setBaseUser(firebaseUser);

            // Load the backend role so role gates (superadmin routes, editor
            // navigation, project permissions) actually receive a value.
            // Guarded by uid because onIdTokenChanged also fires on routine
            // hourly token refreshes, and the role cannot change under a
            // stable uid without an explicit refreshUserRole().
            if (roleLoadedForUidRef.current !== firebaseUser.uid) {
              roleLoadedForUidRef.current = firebaseUser.uid;
              loadUserRole(firebaseUser.uid);
            }
            
            // Initialize tab-coordinated session management
            sessionManager?.initializeSession?.(
              firebaseUser,
              (token: string) => {
                // Token refreshed callback - no additional action needed
                debugLog('Token refreshed via tab coordination');
              },
              () => {
                // Session expired callback
                debugLog('Session expired via tab coordination');
                setSessionExpired(true);
              },
              () => {
                // Another tab logged out. Firebase would sync the sign-out
                // here eventually; doing it now stops this tab refreshing
                // (and so re-persisting) the user in the meantime.
                clearUser();
                signOut(auth).catch((signOutError) => {
                  console.warn('Failed to sign out after another tab logged out:', signOutError);
                  sessionManager?.cancelLogout?.();
                });
                client.clearStore().catch((cacheError) => {
                  console.warn('Failed to clear Apollo cache after another tab logged out:', cacheError);
                });
              }
            );
          } else {
            // User exists but email not verified
            // Don't sign out - keep auth.currentUser alive for resend-verification flow
            // Setting user to null blocks access via PrivateRoute
            debugLog('User email not verified, restricting access');
            clearUser();
          }
        } else {
          // Signed out: a later sign-in (even as the same user) gets its own
          // one verification reload.
          verificationReloadedForUidRef.current = null;
          clearUser();
          sessionManager?.cleanup?.();
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        clearUser();
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [isE2EAuthBypassEnabled, sessionManager, clearUser, loadUserRole]);

  // Back/forward cache. A page restored from it comes back exactly as it was
  // left - including a dashboard rendered for a user who has since logged
  // out in another page load or tab - and no route guard runs again. Reload
  // it so auth is read afresh and PrivateRoute can redirect.
  useEffect(() => {
    if (isE2EAuthBypassEnabled) return;
    const handlePageShow = (event: PageTransitionEvent) => {
      if (shouldReloadRestoredPage({
        persisted: event.persisted,
        hasCurrentUser: Boolean(auth.currentUser),
        lastSignedInSeenAt: lastSignedInSeenAtRef.current,
        loggedOutAt: readLoggedOutAt(),
      })) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isE2EAuthBypassEnabled]);




  // Enhanced token retrieval with automatic refresh and expiry detection
  // Memoised on `user`. As a plain function it was new on every render, and
  // calling it re-renders this provider (setLastActivity), so no consumer
  // could safely list it as an effect dependency.
  const getToken = useCallback(async (forceRefresh: boolean = false): Promise<string | null> => {
    if (!user) {
      return null;
    }

    try {
      // Get token with optional force refresh
      const token = await user.getIdToken(forceRefresh);
      
      // Reset session expired state on successful token retrieval (a no-op
      // render-wise when it is already false).
      setSessionExpired(false);
      
      return token;
    } catch (error: any) {
      console.error('Error getting token:', error);
      
      // Check if error indicates expired session
      if (error.code === 'auth/user-token-expired' || 
          error.code === 'auth/invalid-user-token' ||
          error.message?.includes('expired') ||
          error.message?.includes('invalid')) {
        
        console.warn('Session expired, marking for user notification');
        setSessionExpired(true);
        return null;
      }
      
      // For other errors, try to refresh token once
      if (!forceRefresh) {
        debugLog('Attempting token refresh due to error');
        return await getToken(true);
      }
      
      return null;
    }
  }, [user]);

  const refreshUserRole = useCallback(async (): Promise<void> => {
    if (baseUser) {
      await loadUserRole(baseUser.uid);
    }
  }, [baseUser, loadUserRole]);

  const roleLoading = Boolean(baseUser) && roleSettledForUid !== baseUser?.uid;

  // Session management functions
  const handleSessionExpiry = useCallback(() => {
    debugLog('Handling session expiry - logging out user');
    setSessionExpired(false);
    clearUser();
    // Same path as the explicit logouts: other tabs sign out too, and cached
    // GraphQL results don't survive into the next session.
    performLogout().catch((logoutError) => {
      console.warn('Failed to sign out on session expiry:', logoutError);
    });
  }, [clearUser]);

  const refreshSession = useCallback(async (): Promise<void> => {
    if (!user) {
      throw new Error('No active session to refresh');
    }
    // getToken swallows failures and resolves to null, so an unchecked await
    // here would always report success and re-open the expiry dialog forever.
    const token = await getToken(true);
    if (!token) {
      setSessionExpired(true);
      throw new Error('Session refresh failed');
    }
    setSessionExpired(false);
  }, [user, getToken]);

  // Tab-coordinated token refresh is now handled by TabCoordinatedSessionManager
  // This removes the resource waste of multiple timers per user across tabs

  // An inactivity timeout used to live here as commented-out code, fed by a
  // `lastActivity` state that every getToken() call updated - re-rendering
  // this provider (and every useAuth() consumer) on each token fetch for a
  // feature that was switched off. If one is added, track activity in a ref.

  // One object per actual change: an inline object literal was new on every
  // render, so every useAuth() consumer re-rendered whenever this did.
  // `userRole` is listed because `user` keeps the same (Firebase) object
  // identity when its role loads - see the `user` memo above - so without it
  // consumers would never see the role.
  const contextValue = useMemo(
    () => ({ user, loading, roleLoading, getToken, refreshUserRole, sessionExpired, handleSessionExpiry, refreshSession }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, userRole, loading, roleLoading, getToken, refreshUserRole, sessionExpired, handleSessionExpiry, refreshSession]
  );

  // Listen for session expiry events from Apollo Client
  useEffect(() => {
    const handleSessionExpiredEvent = (event: CustomEvent) => {
      debugLog('Session expired event received:', event.detail);
      setSessionExpired(true);
    };

    window.addEventListener('session-expired', handleSessionExpiredEvent as EventListener);
    
    return () => {
      window.removeEventListener('session-expired', handleSessionExpiredEvent as EventListener);
    };
  }, []);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => useContext(AuthContext);
